import { FormEvent, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { dtpApi } from '../../lib/dtpApi';
import { useDtpAuthStore } from '../../store/useDtpAuthStore';
import { Link } from 'react-router-dom';
import { ChevronRight, CreditCard, MapPin, User } from 'lucide-react';
import { SignInMethods } from '../../components/dtp/SignInMethods';
import { NotificationPreferences } from '../../components/dtp/NotificationPreferences';
import { dtpPath, useDtpParams } from '../../lib/clinicSite';

type ProfileDraft = {
  firstName: string; lastName: string; email: string;
  street: string; street2: string; city: string; state: string; zipCode: string;
};
const emptyDraft: ProfileDraft = { firstName: '', lastName: '', email: '', street: '', street2: '', city: '', state: '', zipCode: '' };

export function DtpAccount() {
  const [draft, setDraft] = useState(emptyDraft);
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('US');
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const patientId = useDtpAuthStore(s => s.patient?._id);
  const { physicianId } = useDtpParams();
  const queryClient = useQueryClient();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoaded(false);
    setError('');
    dtpApi.getPatientProfile().then(res => {
      if (cancelled) return;
      if (!res.success || !res.patient) throw new Error('Profile unavailable');
      const p = res.patient;
      setDraft({ firstName: p.firstName || '', lastName: p.lastName || '', email: p.email || '', street: p.address?.street || '', street2: p.address?.street2 || '', city: p.address?.city || '', state: p.address?.state || '', zipCode: p.address?.zipCode || '' });
      setPhone(p.phone || '');
      setCountry(p.address?.country || 'US');
      setLoaded(true);
    }).catch(() => { if (!cancelled) setError('We couldn’t load your account. Please try again.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [patientId, attempt]);

  async function save(event: FormEvent) {
    event.preventDefault();
    setSaving(true); setError(''); setSaved(false);
    try {
      const { firstName, lastName, email, street, street2, city, state, zipCode } = draft;
      if (![firstName, lastName, email, street, city, state, zipCode].every(value => value.trim())) throw new Error('Complete all required fields.');
      const res = await dtpApi.updatePatientProfile({ firstName: firstName.trim(), lastName: lastName.trim(), email: email.trim(), address: { street: street.trim(), street2: street2.trim(), city: city.trim(), state: state.trim().toUpperCase(), zipCode: zipCode.trim(), country } });
      if (!res.success || !res.patient) throw new Error(res.error || 'Could not save your account.');
      const auth = useDtpAuthStore.getState();
      if (auth.patient?._id === patientId) useDtpAuthStore.setState({ patient: { ...auth.patient, ...res.patient } });
      await queryClient.invalidateQueries({ queryKey: ['patient-home'] });
      setSaved(true);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not save your account. Please try again.');
    } finally { setSaving(false); }
  }

  function field(name: keyof ProfileDraft, label: string, autoComplete: string, optional = false, maxLength?: number) {
    return <label className="block text-sm font-medium text-[var(--portal-ink)]">{label}{optional ? ' (optional)' : ''}
      <input name={name} autoComplete={autoComplete} required={!optional} type={name === 'email' ? 'email' : 'text'} maxLength={maxLength} minLength={name === 'state' ? 2 : name === 'zipCode' ? 5 : undefined} value={draft[name]} onChange={e => { setDraft(prev => ({ ...prev, [name]: e.target.value })); setSaved(false); }} className="mt-2 block h-12 w-full rounded-2xl border border-[rgba(36,26,21,0.12)] bg-white/90 px-4 font-normal transition focus:!border-[rgba(224,86,58,0.5)] focus:outline-none focus:ring-4 focus:!ring-[rgba(224,86,58,0.15)]" />
    </label>;
  }
  const initials = `${draft.firstName.trim()[0] || ''}${draft.lastName.trim()[0] || ''}`.toUpperCase();
  return <div className="mx-auto max-w-2xl">
    <h1 className="portal-display text-[36px] font-bold leading-none sm:text-[44px]">Your account</h1>
    <p className="mt-3 text-[17px] text-[var(--portal-muted)]">Keep your contact details and shipping address up to date.</p>
    {loaded && <div className="portal-card mt-8 flex items-center gap-4 p-5 sm:p-6">
      <span aria-hidden="true" className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-[var(--portal-ink)] text-lg font-semibold text-[var(--portal-card-solid)]">{initials || '·'}</span>
      <div className="min-w-0">
        <p className="portal-display truncate text-xl font-semibold">{[draft.firstName, draft.lastName].filter(Boolean).join(' ') || 'Your profile'}</p>
        <p className="truncate text-sm text-[var(--portal-muted)]">{[phone, draft.email].filter(Boolean).join(' · ')}</p>
      </div>
    </div>}
    {loading ? <p role="status" className="mt-8">Loading your account…</p> : <>
      {!loaded ? <div role="alert" className="mt-6 text-red-700">{error} <button className="underline min-h-[44px]" onClick={() => setAttempt(n => n + 1)}>Try again</button></div> : <form onSubmit={save} className="portal-card mt-5 space-y-8 p-6 sm:p-8">
        <fieldset disabled={saving} className="space-y-5">
          <legend className="portal-display mb-5 flex items-center gap-3 text-xl font-semibold"><span aria-hidden="true" className="grid h-9 w-9 place-items-center rounded-xl bg-[var(--portal-tint-1)]"><User className="h-[18px] w-[18px]" /></span>Contact details</legend>
          <div className="grid gap-5 sm:grid-cols-2">{field('firstName', 'First name', 'given-name', false, 80)}{field('lastName', 'Last name', 'family-name', false, 80)}</div>
          {field('email', 'Email', 'email')}
          <div className="text-sm"><p className="font-medium text-[var(--portal-ink)]">Sign-in phone</p><p className="mt-1 text-[var(--portal-muted)]">{phone || 'Not available'}</p><p className="mt-2 text-[var(--portal-muted)]">Contact your clinic if your sign-in number needs to change.</p></div>
        </fieldset>
        <hr className="border-[var(--portal-line)]" />
        <fieldset disabled={saving} className="space-y-5">
          <legend className="portal-display mb-5 flex items-center gap-3 text-xl font-semibold"><span aria-hidden="true" className="grid h-9 w-9 place-items-center rounded-xl bg-[var(--portal-tint-3)]"><MapPin className="h-[18px] w-[18px]" /></span>Shipping address</legend>
          <p className="text-sm text-[var(--portal-muted)]">Used for future orders. Contact your clinic about an order already placed.</p>
          {field('street', 'Street address', 'address-line1')}
          {field('street2', 'Apartment or suite', 'address-line2', true, 35)}
          {field('city', 'City', 'address-level2')}
          <div className="grid gap-5 sm:grid-cols-2">{field('state', 'State (2-letter code)', 'address-level1', false, 2)}{field('zipCode', 'ZIP code', 'postal-code')}</div>
        </fieldset>
        {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
        {saved && <p role="status" className="text-sm text-green-700">Your account has been updated.</p>}
        <button disabled={saving} className="h-12 rounded-full bg-[var(--portal-primary)] px-7 text-sm font-semibold text-[var(--portal-primary-fg)] transition hover:opacity-90 disabled:opacity-50">{saving ? 'Saving…' : 'Save changes'}</button>
      </form>}
      {loaded && <NotificationPreferences />}
      {loaded && <SignInMethods />}
      {loaded && <Link to={dtpPath(physicianId, '/billing')} className="portal-card group mt-5 flex items-center justify-between gap-4 p-5 transition hover:bg-white sm:p-6">
        <span className="flex items-center gap-4">
          <span aria-hidden="true" className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-[var(--portal-tint-1)]"><CreditCard className="h-5 w-5" /></span>
          <span><span className="portal-display block text-lg font-semibold">Payment methods & receipts</span><span className="block text-sm text-[var(--portal-muted)]">Manage saved cards and see what you’ve paid.</span></span>
        </span>
        <ChevronRight aria-hidden="true" className="h-5 w-5 shrink-0 text-[var(--portal-muted)] transition group-hover:translate-x-0.5" />
      </Link>}
    </>}
  </div>;
}
